import React, { FC, useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";

import { DateInput, GridLineBreak, LocalityInput, SelectInput, TextInput } from "@/components/atoms";
import { RefSelectInput } from "@/components/molecules";
import { defaultDateFormat } from "@/core/date";
import { FamilyMember, IdNamePair, Relation } from "@/shared/domain";
import { AddressMaxLength } from "@/validations/address";

import { formSchema } from "./formSchema";
import { ChangeAddressFormType } from "./formType";
import { StyledAddressChangeButton, StyledAddressChangeForm } from "./styled";

type RequestAddressChangeFormProps = {
    pendingAddressChange: MyBrokerApi.AddressInfo;
    relations: Relation[];
    countries: IdNamePair[];
    onSubmit: (formData: MyBrokerApi.AddressChangeRequest) => void;
};

const mapEntity = (source: MyBrokerApi.AddressInfo): ChangeAddressFormType => {
    return {
        mainAddress: source?.addressDetail?.mainAddress || "",
        additionalAddress: source?.addressDetail?.additionalAddress || "",
        number: source?.addressDetail?.number || "",
        mailBox: source?.addressDetail?.mailBox || "",
        postCode: source?.addressDetail?.postCode || "",
        city: source?.addressDetail?.city || "",
        country: source?.addressDetail?.country?.id || "",
        activationDate: source?.addressDetail?.activationDate || null,
        concerning: source?.familyMembers?.map((familyMember) => familyMember.id) || [],
    };
};

const getRelationLabel = (relation: FamilyMember) => {
    return `${relation.fullName} (${relation.relationType?.name}${
        relation.birthdate ? ` - ${defaultDateFormat(relation.birthdate)}` : ""
    })`;
};

export const getRelationsPairId = (relations: FamilyMember[]): IdNamePair<number>[] =>
    relations.map((relation) => ({
        id: relation.id,
        name: getRelationLabel(relation),
    }));

const RequestAddressChangeForm: FC<RequestAddressChangeFormProps> = ({
    pendingAddressChange,
    relations,
    countries,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const { handleSubmit, control, reset, formState } = useForm<ChangeAddressFormType>({
        mode: "onBlur",
        defaultValues: mapEntity(pendingAddressChange),
        resolver: yupResolver(formSchema()),
    });

    useEffect(() => {
        reset(mapEntity(pendingAddressChange));
    }, [pendingAddressChange]);

    const submitHandler = async (formData: ChangeAddressFormType) => {
        const selectedFamilyMembers = relations.filter((relation) => (formData.concerning || []).includes(relation.id));
        const selectedCountry = countries.find((country) => country.id.toString() === formData.country);
        onSubmit({
            pendingAddress: {
                addressDetail: {
                    mainAddress: formData.mainAddress,
                    additionalAddress: formData.additionalAddress,
                    number: formData.number,
                    mailBox: formData.mailBox,
                    postCode: formData.postCode,
                    city: formData.city,
                    country: selectedCountry,
                    activationDate: formData.activationDate,
                },
                familyMembers: selectedFamilyMembers,
            },
        });
    };
    return (
        <StyledAddressChangeForm>
            <form onSubmit={handleSubmit(submitHandler)}>
                <Grid container>
                    <Grid item xs={12} sm={8}>
                        <TextInput
                            name="mainAddress"
                            label={t("Common.Address")}
                            required
                            autoComplete="off"
                            maxLength={AddressMaxLength.Street}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextInput
                            name="number"
                            label={t("Common.Number")}
                            required
                            autoComplete="off"
                            maxLength={AddressMaxLength.HouseNumber}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextInput
                            name="mailBox"
                            label={t("Common.Box")}
                            autoComplete="off"
                            control={control}
                            maxLength={AddressMaxLength.MailBox}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextInput
                            name="additionalAddress"
                            label={t("Common.AdditionalAddress")}
                            autoComplete="off"
                            maxLength={AddressMaxLength.AdditionalAddress}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextInput
                            name="postCode"
                            label={t("Common.PostalCode")}
                            required
                            autoComplete="off"
                            maxLength={AddressMaxLength.ZipCode}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <LocalityInput name="city" required control={control} postalCode="postCode" country="country" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <RefSelectInput name="country" required control={control} table="country" />
                    </Grid>
                    <GridLineBreak />
                    <Grid item xs={12} sm={3}>
                        <DateInput name="activationDate" required label={t("Common.EffectiveDate")} control={control} />
                    </Grid>
                </Grid>
                <br />
                {relations?.length > 0 && (
                    <>
                        <div className="subTitle" id="concerns-label">
                            {t("Common.Concerns")}
                        </div>
                        <p className="relationsText">{t("User.RequestAddressChange.Concerned.Text")}</p>
                        <Grid container>
                            <Grid item xs={12} sm={8}>
                                <SelectInput
                                    name="concerning"
                                    control={control}
                                    multiple
                                    options={getRelationsPairId(relations)}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
                <StyledAddressChangeButton type="submit" data-testid="button_confirm" disabled={!formState.isDirty}>
                    {t("Common.Confirm")}
                </StyledAddressChangeButton>
            </form>
        </StyledAddressChangeForm>
    );
};

export default RequestAddressChangeForm;
